import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Box, Button, useTheme, useMediaQuery } from '@mui/material';
import { AddBoxOutlined } from '@mui/icons-material';
import { useGetSitesQuery, useDisableSiteMutation, useEnableSiteMutation, useRemoveSiteMutation } from '../../services/api';
import ErrorSnackBar from '../../components/ErrorSnackbar';
import mainLocationsInQatar from '../../constants/mainLocations';
import Loading from '../../components/Loading';
import SitesHeader from './SitesHeader';
import SitesContent from './SiteContent';
import AddSiteDialogBox from './AddSiteDialogBox';
import useSelectedGroup from '../../hooks/useSelectedGroup';
import useSelectedCompany from '../../hooks/useSelectedCompany';
import useGroupOptions from '../../hooks/useGroupOptions';
import useCompanyOptions from '../../hooks/useCompanyOptions';
import GroupSelector from '../../components/GroupSelector';

const Sites = () => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const { role, company } = useSelector((state) => state.global);
    const isAdmin = role === 'Admin';
    const isSuperOrPowerAdmin = role === 'Super Admin' || role === 'Power Admin';

    const [openDialog, setOpenDialog] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

    // API
    const { data: sitesData, refetch, isError: siteDataError } = useGetSitesQuery(company);
    const [disableSiteMutation] = useDisableSiteMutation();
    const [enableSiteMutation] = useEnableSiteMutation();
    const [removeSiteMutation] = useRemoveSiteMutation();

    // Company and Group option
    const { companyOptions } = useCompanyOptions();
    const groupOptions = useGroupOptions(company);
    const { selectedCompanyId } = useSelectedCompany({ companyOptions });
    const { selectedGroup, handleGroupChange } = useSelectedGroup(groupOptions, isSuperOrPowerAdmin ? selectedCompanyId : company);

    useEffect(() => {
        refetch();
    }, [refetch]);

    const selectedGroupObj = groupOptions.find((group) => group._id === selectedGroup);

    // Filter Data
    const filteredSites = useMemo(() => {
        const selectedSiteIds = selectedGroupObj?.siteIds || [];
        return sitesData?.filter((site) => selectedSiteIds.includes(site._id)) || [];
    }, [sitesData, selectedGroupObj]);

    const handleOpenDialog = () => setOpenDialog(true);
    const handleCloseDialog = () => setOpenDialog(false);
    const handleSiteStatusChange = async (siteId, newStatus) => {
        try {
            if (newStatus === 'disabled') {
                await disableSiteMutation(siteId);
                setSnackbar({ open: true, message: `Site disabled successfully`, severity: 'success' });
            } else if (newStatus === 'enabled') {
                await enableSiteMutation(siteId);
                setSnackbar({ open: true, message: `Site enabled successfully`, severity: 'success' });
            }
            refetch();
        } catch (error) {
            console.error('Error updating site status:', error);
            setSnackbar({ open: true, message: 'Failed to update site status. Please try again later.', severity: 'error' });
        }
    };

    // Handle remove site
    const handleRemoveSite = async (siteId) => {
        try {
            const site = sitesData?.find((site) => site._id === siteId);

            if (!site) {
                setSnackbar({ open: true, message: 'Site not found.', severity: 'error' });
                return;
            }

            if (site.devices > 0 || site.connectedMACs.length > 0) {
                setSnackbar({ open: true, message: 'Cannot remove site when a device is connected.', severity: 'error' });
                return;
            }

            await removeSiteMutation(siteId);
            refetch();
            setSnackbar({ open: true, message: 'Site removed successfully', severity: 'success' });
        } catch (error) {
            console.error('Error removing site:', error);
            setSnackbar({ open: true, message: 'Failed to remove site. Please try again later.', severity: 'error' });
        }
    };

    const hasGroups = Array.isArray(groupOptions) && groupOptions.length > 0;

    return (
        <Box m={isSmallScreen ? '1rem' : '1rem 2.5rem'}>
            <SitesHeader handleOpenDialog={handleOpenDialog} isSmallScreen={isSmallScreen} selectedGroup={selectedGroup} handleGroupChange={handleGroupChange} groupOptions={groupOptions} />

            {isSmallScreen && (
                // Mobile view
                <Box
                    display="flex"
                    flexDirection="column"
                    gap="1rem"
                    marginBottom="1rem"
                    padding="1rem"
                    backgroundColor={theme.palette.background.paper}
                    borderRadius="15px"
                    sx={{
                        boxShadow: '8px 8px 20px rgba(0, 0, 0, 0.1)',
                    }}
                >
                    {hasGroups && <GroupSelector selectedGroup={selectedGroup} handleGroupChange={handleGroupChange} groupOptions={groupOptions} />}
                    {isAdmin && (
                        <Button
                            onClick={handleOpenDialog}
                            sx={{
                                backgroundColor: theme.palette.text.white,
                                color: theme.palette.text.black,
                                padding: '1rem',
                                width: '100%',
                                '&:hover': { backgroundColor: theme.palette.text.white },
                                '&:focus': { outline: 'none' },
                            }}
                        >
                            <AddBoxOutlined sx={{ marginRight: '8px' }} />
                            Add Site
                        </Button>
                    )}
                </Box>
            )}

            {siteDataError ? (
                <Loading />
            ) : (
                <SitesContent
                    sitesData={filteredSites}
                    siteDataError={siteDataError}
                    isSmallScreen={isSmallScreen}
                    handleSiteStatusChange={handleSiteStatusChange}
                    handleRemoveSite={handleRemoveSite}
                />
            )}
            <AddSiteDialogBox
                openDialog={openDialog}
                handleCloseDialog={handleCloseDialog}
                setSnackbar={setSnackbar}
                sitesData={filteredSites}
                mainLocationsInQatar={mainLocationsInQatar}
                refetch={refetch}
            />
            <ErrorSnackBar open={snackbar.open} onClose={() => setSnackbar({ ...snackbar, open: false })} message={snackbar.message} severity={snackbar.severity} />
        </Box>
    );
};

export default Sites;
